import * as z from 'zod';
import { sepaSectionSchema } from '../../../offer-checkout/components/offer-checkout-form/offer-checkout-form.schema';
import { PaymentMethod } from '../../../offer-checkout/constants/payment-method.enum';
import { compactBankValue } from '../../../offer-checkout/lib/format';
import { centsToEuroInput } from '../../../products/lib/money';
import { CorrectOfferRequestDto } from '../../types/request';
import { OfferResponseDto } from '../../types/response';
import { offerFormSchema, OfferCustomerFieldName, OfferFormValues, toCreateOfferRequest } from '../offer-form/offer-form.schema';

// Complete like on accept – the invoice mail stays optional, the mail is not editable.
const REQUIRED_CUSTOMER_FIELDS: Partial<Record<OfferCustomerFieldName, string>> = {
  firstName: 'Vorname ist erforderlich',
  lastName: 'Nachname ist erforderlich',
  name: 'Firma ist erforderlich',
  vatId: 'USt-ID ist erforderlich',
  street: 'Straße ist erforderlich',
  houseNumber: 'Hausnummer ist erforderlich',
  postCode: 'PLZ ist erforderlich',
  city: 'Ort ist erforderlich',
  country: 'Land ist erforderlich',
  phone: 'Telefon ist erforderlich',
  newCustomerDate: 'Bitte ein Startdatum wählen'
};

/** The offer form's rules plus the accept's: every customer field required, IBAN and BIC for SEPA. Same values type. */
export const offerCorrectionSchema = offerFormSchema.superRefine(({ customer, paymentMethod }, ctx) => {
  Object.entries(REQUIRED_CUSTOMER_FIELDS).forEach(([field, message]) => {
    if (!customer[field as OfferCustomerFieldName].trim()) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['customer', field], message });
  });

  if (paymentMethod !== PaymentMethod.SEPA) return;
  const result = sepaSectionSchema.safeParse(customer);
  if (!result.success) result.error.issues.forEach((issue) => ctx.addIssue({ ...issue, path: ['customer', ...issue.path] }));
});

/** The accepted offer as form values – nothing is left out, the options contain the contract's product and add-ons. */
export const toOfferCorrectionValues = ({ product, addOns, paymentMethod, customer }: OfferResponseDto): OfferFormValues => ({
  term: product.term,
  productId: product.productId,
  title: product.title,
  description: product.description,
  monthlyPrice: centsToEuroInput(product.monthlyPriceCents),
  additionalConditions: product.additionalConditions ?? '',
  addOnIds: addOns.map(({ addOnId }) => addOnId),
  paymentMethod,
  customer: {
    mail: customer.mail,
    firstName: customer.firstName ?? '',
    lastName: customer.lastName ?? '',
    name: customer.name ?? '',
    vatId: customer.vatId ?? '',
    street: customer.street ?? '',
    houseNumber: customer.houseNumber ?? '',
    postCode: customer.postCode ?? '',
    city: customer.city ?? '',
    country: customer.country ?? '',
    invoiceMail: customer.invoiceMail ?? '',
    phone: customer.phone ?? '',
    newCustomerDate: customer.newCustomerDate ?? '',
    iban: customer.iban ?? '',
    bic: customer.bic ?? ''
  }
});

/** Product part like the offer form; the customer without the mail, IBAN and BIC only for SEPA. */
export const toCorrectOfferRequest = (values: OfferFormValues): CorrectOfferRequestDto => {
  const { customer } = values;
  const isSepa = values.paymentMethod === PaymentMethod.SEPA;

  return {
    ...toCreateOfferRequest(values),
    customer: {
      firstName: customer.firstName.trim(),
      lastName: customer.lastName.trim(),
      name: customer.name.trim(),
      vatId: customer.vatId.trim(),
      street: customer.street.trim(),
      houseNumber: customer.houseNumber.trim(),
      postCode: customer.postCode.trim(),
      city: customer.city.trim(),
      country: customer.country,
      phone: customer.phone.trim(),
      newCustomerDate: customer.newCustomerDate,
      invoiceMail: customer.invoiceMail.trim() || undefined,
      ...(isSepa && { iban: compactBankValue(customer.iban), bic: compactBankValue(customer.bic) })
    }
  };
};
